import React, { useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from 'sonner';
import ChatWidget from './components/ChatWidget';

// Pages
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ProductsPage from './pages/ProductsPage';
import ProductDetailPage from './pages/ProductDetailPage';
import SuppliersPage from './pages/SuppliersPage';
import SupplierProfilePage from './pages/SupplierProfilePage';
import RFQsPage from './pages/RFQsPage';
import RFQDetailPage from './pages/RFQDetailPage';
import CreateRFQPage from './pages/CreateRFQPage';
import MembershipPage from './pages/MembershipPage';
import MembershipSuccessPage from './pages/MembershipSuccessPage';
import SearchResultsPage from './pages/SearchResultsPage';

// Dashboard Pages
import SupplierDashboard from './pages/dashboard/SupplierDashboard';
import BuyerDashboard from './pages/dashboard/BuyerDashboard';
import AddProductPage from './pages/dashboard/AddProductPage';
import MessagesPage from './pages/dashboard/MessagesPage';
import AdminDashboard from './pages/dashboard/AdminDashboard';
import AgentDashboard from './pages/dashboard/AgentDashboard';
import PremiumChannelPage from './pages/PremiumChannelPage';
import ChannelAdminPage from './pages/dashboard/ChannelAdminPage';
import BillingPage from './pages/dashboard/BillingPage';
import VerificationAdminPage from './pages/dashboard/VerificationAdminPage';
import NotificationsPage from './pages/NotificationsPage';
import NotificationSettingsPage from './pages/NotificationSettingsPage';
import { registerServiceWorker } from './services/pushManager';

// Auth Callback for Google OAuth
const AuthCallback = () => {
  const navigate = useNavigate();
  const { handleGoogleCallback } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const params = new URLSearchParams(hash.slice(1));
    const sessionId = params.get('session_id');

    if (sessionId) {
      handleGoogleCallback(sessionId).then((result) => {
        if (result.success) {
          navigate('/dashboard', { replace: true });
        } else {
          navigate('/login', { replace: true, state: { error: result.error } });
        }
      });
    } else {
      navigate('/login', { replace: true });
    }
  }, [handleGoogleCallback, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-slate-200 border-t-blue-600 mx-auto mb-4"></div>
        <p className="text-slate-600">Completing sign in...</p>
      </div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, allowedTypes = [] }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-slate-200 border-t-blue-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (allowedTypes.length > 0 && !allowedTypes.includes(user.user_type) && user.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Dashboard redirect based on user type
const DashboardRedirect = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-slate-200 border-t-blue-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.role === 'admin') {
    return <Navigate to="/admin" replace />;
  }

  if (user.user_type === 'supplier') {
    return <Navigate to="/dashboard/supplier" replace />;
  }

  if (user.user_type === 'agent') {
    return <Navigate to="/dashboard/agent" replace />;
  }

  return <Navigate to="/dashboard/buyer" replace />;
};

// App Router
const AppRouter = () => {
  const location = useLocation();

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Check URL fragment for session_id (Google OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<HomePage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/products" element={<ProductsPage />} />
      <Route path="/products/:productId" element={<ProductDetailPage />} />
      <Route path="/suppliers" element={<SuppliersPage />} />
      <Route path="/suppliers/:userId" element={<SupplierProfilePage />} />
      <Route path="/rfqs" element={<RFQsPage />} />
      <Route path="/rfqs/:rfqId" element={<RFQDetailPage />} />
      <Route path="/search" element={<SearchResultsPage />} />
      <Route path="/membership" element={<MembershipPage />} />
      <Route path="/membership/success" element={<MembershipSuccessPage />} />

      {/* Premium Channel - Public (content gated inside) */}
      <Route path="/channel" element={<PremiumChannelPage />} />
      <Route path="/channel/:postId" element={<PremiumChannelPage />} />

      {/* Notifications - Protected */}
      <Route path="/notifications" element={<NotificationsPage />} />
      <Route path="/notifications/settings" element={<NotificationSettingsPage />} />

      {/* Protected Routes */}
      <Route path="/dashboard" element={<DashboardRedirect />} />
      
      <Route
        path="/dashboard/supplier"
        element={
          <ProtectedRoute allowedTypes={['supplier']}>
            <SupplierDashboard />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/dashboard/buyer"
        element={
          <ProtectedRoute>
            <BuyerDashboard />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/dashboard/agent"
        element={
          <ProtectedRoute allowedTypes={['agent']}>
            <AgentDashboard />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/dashboard/products/add"
        element={
          <ProtectedRoute allowedTypes={['supplier']}>
            <AddProductPage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/dashboard/products/edit/:productId"
        element={
          <ProtectedRoute allowedTypes={['supplier']}>
            <AddProductPage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/dashboard/messages"
        element={
          <ProtectedRoute>
            <MessagesPage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/rfqs/create"
        element={
          <ProtectedRoute>
            <CreateRFQPage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/admin"
        element={
          <ProtectedRoute allowedTypes={['admin']}>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/admin/channel"
        element={
          <ProtectedRoute allowedTypes={['admin']}>
            <ChannelAdminPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/verification"
        element={
          <ProtectedRoute allowedTypes={['admin']}>
            <VerificationAdminPage />
          </ProtectedRoute>
        }
      />

      {/* Billing */}
      <Route
        path="/dashboard/billing"
        element={
          <ProtectedRoute>
            <BillingPage />
          </ProtectedRoute>
        }
      />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  useEffect(() => {
    registerServiceWorker();
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors closeButton />
        <AppRouter />
        <ChatWidget />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
